// mig 313 — Quién lleva una sesión, para quien la mira.
//
// La agenda del entrenador tiene DOS vistas —la lista y la rejilla— y hasta
// aquí cada una tenía su propia idea de «mía»: la lista se calculaba el
// entrenador efectivo en cliente con un COALESCE copiado
// (`sessions.monitor_id ?? groups.monitor_id`) y la rejilla comparaba contra el
// `monitor_id` del RPC. Dos copias de la misma regla, y la de la lista se quedó
// corta: no miraba `special_classes.monitor_id`, la tercera vía.
//
// Ahora la regla sale del servidor (`get_trainer_sessions` devuelve el
// entrenador efectivo por las tres vías, y su nombre) y de aquí sale la lectura
// de esa fila. Una sola respuesta, o vuelven a divergir.
//
// ⚠️ GEMELO DE `app/src/lib/quienLleva.ts`, Y AHORA VIGILADO.
//
// Aquí decía «espeja `app/src/lib/agendaData.ts` … si cambia una, cambia la
// otra»: un comentario haciendo el trabajo de una guarda. Y el guard de gemelos
// —`scripts/guard-gemelos-catalogo.mjs`— NO podía emparejarlos, porque empareja
// por RUTA RELATIVA y los dos ficheros no se llamaban igual. Una guarda que no
// puede saltar no protege: lo parece.
//
// La mitad de la app sale de `agendaData.ts` a `app/src/lib/quienLleva.ts` para
// que el guard los empareje, y se comprueba que SALTA separándolos a propósito.
// Si cambia una, cambia la otra, y ahora hay quien lo diga.
/**
 * Fila del RPC `get_trainer_sessions` (mig 177).
 *
 * El helper `rpc` no siempre infiere la fila (ver MonitorGymPage), así que se
 * anota a mano. Desde la mig 280 el RPC devuelve TODAS las sesiones del club en
 * el rango; desde la mig 313, `monitor_id` es el entrenador EFECTIVO por las
 * tres vías —override de la sesión → clase especial → grupo— y viene con
 * `monitor_name` resuelto en servidor.
 */
export interface TrainerSessionRow {
  id: string;
  date: string;
  time_start: string;
  time_end: string;
  title: string;
  sede: string | null;
  kind: string;
  level: string | null;
  status: string;
  group_id: string | null;
  is_special_class: boolean;
  monitor_id: string | null;
  /** mig 313. NULL si no la lleva nadie, y también si el perfil ya no existe. */
  monitor_name?: string | null;
}

export interface QuienLleva {
  /** La lleva quien está mirando. */
  mia: boolean;
  /** No la lleva NADIE por ninguna de las tres vías (mig 313). */
  sinAsignar: boolean;
  /** Nombre completo del que la lleva, si se sabe. */
  nombre: string | null;
}

export function quienLleva(
  s: Pick<TrainerSessionRow, 'monitor_id' | 'monitor_name'>,
  trainerId: string
): QuienLleva {
  const nombre = s.monitor_name?.trim() ? s.monitor_name.trim() : null;
  return {
    mia: s.monitor_id != null && s.monitor_id === trainerId,
    sinAsignar: s.monitor_id == null,
    nombre,
  };
}

/**
 * Lo que se lee en el chip.
 *
 * ⚠️ «Sin asignar» sale SOLO cuando la sesión no la lleva nadie, nunca cuando
 * la lectura falló: quien llama pinta el chip únicamente sobre datos que
 * llegaron. Y un `monitor_id` sin nombre —perfil borrado— no puede dejar el
 * chip mudo ni hacerse pasar por «sin asignar»: sí hay alguien, solo que no
 * sabemos quién.
 */
export function etiquetaQuienLleva(q: QuienLleva): string {
  if (q.sinAsignar) return 'Sin asignar';
  return q.nombre ?? 'Entrenador';
}

/**
 * El nombre corto, para la rejilla: un bloque de 60 minutos ya compite con el
 * nombre del grupo, y el TimeGrid recorta por la derecha. «Alex Pardo» → «Alex».
 */
export function nombreDePila(nombre: string | null): string | null {
  if (!nombre) return null;
  const limpio = nombre.trim();
  if (!limpio) return null;
  return limpio.split(/\s+/)[0];
}

/**
 * ¿La canceló el club? (`sessions.status = 'cancelled'`.)
 *
 * ⚠️ LA PRECEDENCIA, COPIADA LITERAL DE LA PANTALLA DEL ALUMNO
 * (`app/src/screens/alumno/MySessionsScreen.tsx`), que es la única superficie
 * que ya lo hacía bien:
 *
 *     «La sesión cancelada por el club manda sobre el estado del enrollment.»
 *
 * Aquí el «estado del enrollment» es el badge de asistencias —«N pend.» o
 * «Listo»—, así que manda igual: una sesión cancelada se pinta **Cancelada**, y
 * no se pinta el recuento ni el «Listo». Y NO se oculta, también como en la del
 * alumno: el entrenador tiene que verla para entender por qué no hay nadie.
 *
 * El dato ya llegaba. `get_trainer_sessions` devuelve `s.status` y no filtra por
 * él (mig 313), y los dos tipos de fila lo declaran. Lo que faltaba es que
 * cruzara al modelo de vista: se tiraba en el mapeo, una línea antes de
 * pintarse.
 */
export function esCanceladaPorElClub(s: Pick<TrainerSessionRow, 'status'>): boolean {
  return s.status === 'cancelled';
}

/** El rótulo, copiado de `MySessionsScreen`. No se abrevia ni se reescribe. */
export const ETIQUETA_CANCELADA = 'Cancelada';

/**
 * Los colores, copiados de `getStatusStyle` de `MySessionsScreen`: el mismo
 * rojo que ve la familia. Que las dos caras del mismo hecho se vean iguales no
 * es estética — es que son el mismo hecho.
 */
export const COLORES_CANCELADA = {
  bg: '#FF444420',
  border: '#FF444460',
  text: '#FF4444',
} as const;

/**
 * El bloque de la REJILLA, con la misma precedencia aplicada.
 *
 * Las dos rejillas —`MonitorGroupsScreen` en la app, la vista de rejilla de
 * `MonitorAgendaPage` en el panel— mapean TODAS las filas que devuelve
 * `get_trainer_sessions`, y ninguna filtra por estado: medido. Así que una
 * sesión cancelada se pintaba como un bloque igual que los demás, con el color
 * de su nivel. Eso es, literalmente, «se le pinta al entrenador como normal».
 *
 * La precedencia es la del alumno: manda sobre el color del grupo y sobre el
 * gris de «no es tuya». Y el rótulo va DELANTE del título porque el TimeGrid
 * recorta por la derecha: puesto detrás, en un bloque de 60 minutos sería lo
 * primero que desaparece.
 *
 * Vive aquí, y no en cada rejilla, para que el separador y el ORDEN no puedan
 * divergir: `Cancelada · Alevín A` en una y `Alevín A (cancelada)` en la otra
 * sería el mismo fallo de siempre a menor escala.
 */
export function bloqueDeRejilla(
  s: Pick<TrainerSessionRow, 'status'>,
  base: { title: string; color: string }
): { title: string; color: string } {
  if (!esCanceladaPorElClub(s)) return base;
  return { title: `${ETIQUETA_CANCELADA} · ${base.title}`, color: COLORES_CANCELADA.text };
}
